import { useState, useEffect } from "react";
import { auth, db, storage } from "./firebase";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "./components/ConfirmModal";
import Toast from "./components/Toast";
import imageCompression from "browser-image-compression";
import GarageEditor from "./components/GarageEditor";
import GarageList from "./components/GarageList";
import PageLayout from "./components/PageLayout";
import VehicleInfoModal from "./components/VehicleInfoModal";
import VehiclePlaqueMetadataFields, { EMPTY_PLAQUE_METADATA } from "./components/VehiclePlaqueMetadataFields";
import { ensureVehicleBuildId } from "./services/vehiclePlaques";
import UpgradeLimitModal from "./components/UpgradeLimitModal";
import { canCreateBuild, isProUser } from "./utils/entitlements";
import {
  createEmptyVehicleSpecs,
  normalizeVehicleSpecs,
  normalizeCarStages,
  getCurrentStage,
  mergeCurrentStageIntoCar
} from "./utils/buildStages";

import {
  doc,
  setDoc,
  collection,
  getDocs,
  query,
  where
} from "firebase/firestore";

import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";

function Garage() {
  const [username, setUsername] = useState("");
  const [profileDocId, setProfileDocId] = useState("");
  const navigate = useNavigate();

  const [garageCars, setGarageCars] = useState([]);
  const [vehicleDetailsOpen, setVehicleDetailsOpen] = useState(false);

  const [carMaintenance, setCarMaintenance] = useState([]);
  const [editingMaintenanceId, setEditingMaintenanceId] = useState(null);

  const [editingCarId, setEditingCarId] = useState(null);
  const [carYear, setCarYear] = useState("");
  const [carMake, setCarMake] = useState("");
  const [carModel, setCarModel] = useState("");
  const [plaqueMetadata, setPlaqueMetadata] = useState(EMPTY_PLAQUE_METADATA);
  const [purchaseDate, setPurchaseDate] = useState("");
  const [carDetails, setCarDetails] = useState("");
  const [carStatus, setCarStatus] = useState("Driven");

  const [oemSpecs, setOemSpecs] = useState(createEmptyVehicleSpecs());
  const [phaseSpecs, setPhaseSpecs] = useState(createEmptyVehicleSpecs());

  const [carPerformanceMods, setCarPerformanceMods] = useState("");
  const [carSuspensionMods, setCarSuspensionMods] = useState("");
  const [carWheelTireMods, setCarWheelTireMods] = useState("");
  const [carExteriorMods, setCarExteriorMods] = useState("");
  const [carInteriorMods, setCarInteriorMods] = useState("");
  const [carMiscMods, setCarMiscMods] = useState("");

  const [editingModId, setEditingModId] = useState(null);
  const [carMods, setCarMods] = useState([]);

  const [carImages, setCarImages] = useState([]);
  const [existingCarImages, setExistingCarImages] = useState([]);

  const [hasUnlimitedGarage, setHasUnlimitedGarage] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const [salePrice, setSalePrice] = useState("");
  const [saleMileage, setSaleMileage] = useState("");
  const [saleTitleStatus, setSaleTitleStatus] = useState("");
  const [salePreferredContact, setSalePreferredContact] = useState("");
  const [saleClassifiedsLink, setSaleClassifiedsLink] = useState("");

  const [timelineDate, setTimelineDate] = useState("");
  const [timelineMod, setTimelineMod] = useState("");
  const [carTimeline, setCarTimeline] = useState([]);
  const [editingTimelineId, setEditingTimelineId] = useState(null);

  const [buildOpen, setBuildOpen] = useState(false);

  const modCategories = [
    "Performance Mods",
    "Suspension Mods",
    "Wheel & Tire Mods",
    "Exterior Mods",
    "Interior Mods",
    "Miscellaneous Mods"
  ];

  const [showBuildCost, setShowBuildCost] = useState(false);

  const [toast, setToast] = useState({
    title: "",
    message: "",
    type: "success"
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    danger: true,
    onConfirm: null
  });

  const currentEditingCar =
    garageCars.find((car) => String(car.id) === String(editingCarId)) || null;

  const currentPhaseName =
    currentEditingCar ? getCurrentStage(currentEditingCar)?.name || "Original Build" : "Original Build";

  const showToast = (title, message = "", type = "success") => {
    setToast({ title, message, type });

    setTimeout(() => {
      setToast({
        title: "",
        message: "",
        type: "success"
      });
    }, 3000);
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setProfileLoading(false);
        return;
      }

      try {
        const profileQuery = query(
          collection(db, "profiles"),
          where("userId", "==", user.uid)
        );
        const snapshot = await getDocs(profileQuery);
        const profileDocument = snapshot.docs[0];

        if (!profileDocument) return;

        const data = profileDocument.data();
        const proUser = isProUser(data);

        setProfile(data);
        setProfileDocId(profileDocument.id);
        setUsername(data.username || "");
        setGarageCars((data.garageCars || []).map((car) => normalizeCarStages({ ...car,
          ...(data.vehicleBuildIds?.[String(car.id)] ? { buildId: data.vehicleBuildIds[String(car.id)] } : {}) })));
        setHasUnlimitedGarage(proUser);

        if (proUser) setShowUpgradeModal(false);
      } catch (error) {
        setToast({
          title: "Unable to load garage",
          message: error.message,
          type: "error"
        });
      } finally {
        setProfileLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const clearCarForm = () => {
    setPlaqueMetadata(EMPTY_PLAQUE_METADATA);
    setEditingCarId(null);
    setCarYear("");
    setCarMake("");
    setCarModel("");
    setPurchaseDate("");
    setCarDetails("");
    setCarStatus("Driven");

    setOemSpecs(createEmptyVehicleSpecs());
    setPhaseSpecs(createEmptyVehicleSpecs());

    setCarPerformanceMods("");
    setCarSuspensionMods("");
    setCarWheelTireMods("");
    setCarExteriorMods("");
    setCarInteriorMods("");
    setCarMiscMods("");

    setCarImages([]);
    setExistingCarImages([]);

    setTimelineDate("");
    setEditingTimelineId(null);
    setTimelineMod("");
    setCarTimeline([]);

    setSalePrice("");
    setSaleMileage("");
    setSaleTitleStatus("");
    setSalePreferredContact("");
    setSaleClassifiedsLink("");

    setCarMods([]);
    setEditingModId(null);

    setShowBuildCost(false);

    setCarMaintenance([]);
    setEditingMaintenanceId(null);
  };

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 1920,
      useWebWorker: true
    };

    return await imageCompression(file, options);
  };

  const uploadCarImages = async () => {
    const uploadedCarImages = [];
    const user = auth.currentUser;

    if (!user) {
      throw new Error("You must be logged in to upload car images.");
    }

    for (let i = 0; i < carImages.length; i++) {
      const file = carImages[i];

      const imageRef = ref(
        storage,
        `garageCars/${user.uid}/${Date.now()}-${file.name}`
      );

      const compressedFile = await compressImage(file);
      await uploadBytes(imageRef, compressedFile);

      const url = await getDownloadURL(imageRef);
      uploadedCarImages.push(url);
    }

    return uploadedCarImages;
  };

  const addTimelineEntry = () => {
    if (!timelineDate || !timelineMod.trim()) {
      showToast(
        "Missing Timeline Info",
        "Enter a date and milestone update.",
        "warning"
      );
      return;
    }

    if (editingTimelineId) {
      const updatedTimeline = carTimeline.map((entry) => {
        if (entry.id === editingTimelineId) {
          return {
            ...entry,
            date: timelineDate,
            mod: timelineMod
          };
        }

        return entry;
      });

      setCarTimeline(updatedTimeline);
      setEditingTimelineId(null);
      setTimelineDate("");
      setTimelineMod("");
      showToast("Timeline Updated", "Your milestone has been updated.");
      return;
    }

    const newEntry = {
      id: Date.now(),
      date: timelineDate,
      mod: timelineMod
    };

    setCarTimeline([...carTimeline, newEntry]);
    setTimelineDate("");
    setTimelineMod("");
    showToast("Timeline Added", "Your milestone has been added.");
  };

  const addModEntry = (modPayload) => {
    if (!modPayload) {
      showToast("Enter a part name.", "", "warning");
      return false;
    }

    const now = new Date().toISOString();

    if (editingModId) {
      const updatedMods = carMods.map((mod) => {
        if (mod.id === editingModId) {
          return {
            ...mod,
            ...modPayload,
            id: mod.id,
            createdAt: mod.createdAt || now,
            updatedAt: now
          };
        }

        return mod;
      });

      setCarMods(updatedMods);
      setEditingModId(null);
      showToast("Mod Updated", "Your mod has been updated.");
      return true;
    }

    const newMod = {
      id: Date.now(),
      ...modPayload,
      createdAt: now,
      updatedAt: now
    };

    setCarMods([...carMods, newMod]);
    setEditingModId(null);
    showToast("Mod Added", "Your mod has been added.");
    return true;
  };

  const editModEntry = (mod) => {
    setEditingModId(mod.id);
  };

  const deleteModEntry = (modId) => {
    const updatedMods = carMods.filter((mod) => mod.id !== modId);
    setCarMods(updatedMods);

    if (editingModId === modId) {
      setEditingModId(null);
    }

    showToast("Mod Deleted", "Your mod has been removed.");
  };

  const addMaintenanceEntry = (maintenancePayload) => {
    if (!maintenancePayload) {
      showToast(
        "Missing Maintenance Info",
        "Complete the required maintenance fields.",
        "warning"
      );
      return false;
    }

    const now = new Date().toISOString();

    if (editingMaintenanceId) {
      const updatedMaintenance = carMaintenance.map((item) => {
        if (item.id === editingMaintenanceId) {
          return {
            ...item,
            ...maintenancePayload,
            id: item.id,
            createdAt: item.createdAt || now,
            updatedAt: now
          };
        }

        return item;
      });

      setCarMaintenance(updatedMaintenance);
      setEditingMaintenanceId(null);
      showToast("Maintenance Updated", "Maintenance entry has been updated.");
      return true;
    }

    const newMaintenance = {
      id: Date.now(),
      ...maintenancePayload,
      createdAt: now,
      updatedAt: now
    };

    setCarMaintenance([...carMaintenance, newMaintenance]);
    setEditingMaintenanceId(null);
    showToast("Maintenance Added", "Maintenance entry has been added.");
    return true;
  };

  const editMaintenanceEntry = (item) => {
    setEditingMaintenanceId(item.id);
  };

  const deleteMaintenanceEntry = (maintenanceId) => {
    setCarMaintenance(carMaintenance.filter((item) => item.id !== maintenanceId));

    if (editingMaintenanceId === maintenanceId) {
      setEditingMaintenanceId(null);
    }

    showToast("Maintenance Deleted", "Maintenance entry has been removed.");
  };

  const editTimelineEntry = (entry) => {
    setEditingTimelineId(entry.id);
    setTimelineDate(entry.date || "");
    setTimelineMod(entry.mod || "");
  };

  const deleteTimelineEntry = (entryId) => {
    const updatedTimeline = carTimeline.filter((entry) => entry.id !== entryId);
    setCarTimeline(updatedTimeline);

    if (editingTimelineId === entryId) {
      setEditingTimelineId(null);
      setTimelineDate("");
      setTimelineMod("");
    }

    showToast("Timeline Deleted", "Your milestone has been removed.");
  };

  const moveModUp = (index) => {
    if (index === 0) return;

    const updatedMods = [...carMods];
    const temp = updatedMods[index - 1];

    updatedMods[index - 1] = updatedMods[index];
    updatedMods[index] = temp;

    setCarMods(updatedMods);
  };

  const moveModDown = (index) => {
    if (index === carMods.length - 1) return;

    const updatedMods = [...carMods];
    const temp = updatedMods[index + 1];

    updatedMods[index + 1] = updatedMods[index];
    updatedMods[index] = temp;

    setCarMods(updatedMods);
  };

  const openConfirmModal = ({
    title,
    message,
    confirmText,
    onConfirm
  }) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmText,
      danger: true,
      onConfirm
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      title: "",
      message: "",
      confirmText: "Confirm",
      danger: true,
      onConfirm: null
    });
  };

  const deleteExistingCarImage = async (imageUrl) => {
    try {
      const imageRef = ref(storage, imageUrl);

      await deleteObject(imageRef);

      const updatedImages = existingCarImages.filter(
        (image) => image !== imageUrl
      );

      setExistingCarImages(updatedImages);
      showToast("Photo deleted", "The photo was removed from storage.");
    } catch (error) {
      showToast("Delete failed", error.message, "error");
    }
  };

  const saveVehicleDetails = async () => {
    await addOrUpdateGarageCar();
    setVehicleDetailsOpen(false);
  };

  const addOrUpdateGarageCar = async () => {
    try {
      if (!editingCarId && profileLoading) return;

      if (
        !editingCarId &&
        !canCreateBuild(profile, garageCars.length)
      ) {
        setShowUpgradeModal(true);
        return;
      }

      if (!carYear.trim() || !carMake.trim() || !carModel.trim()) {
        showToast("Enter year, make, and model.", "", "warning");
        return;
      }

      const uploadedCarImages = await uploadCarImages();

      if (editingCarId) {
        const updatedCars = garageCars.map((car) => {
          if (car.id === editingCarId) {
            return mergeCurrentStageIntoCar(
              {
                ...car,
                ...plaqueMetadata,
                year: carYear,
                make: carMake,
                model: carModel,
                purchaseDate,
                name: `${carYear} ${carMake} ${carModel}`,
                details: carDetails,
                status: carStatus,
                showBuildCost,
                oemSpecs: normalizeVehicleSpecs(oemSpecs),
                saleInfo: {
                  price: salePrice,
                  mileage: saleMileage,
                  titleStatus: saleTitleStatus,
                  preferredContact: salePreferredContact,
                  classifiedsLink: saleClassifiedsLink
                }
              },
              {
                images: [...existingCarImages, ...uploadedCarImages],
                mods: carMods,
                timeline: carTimeline,
                maintenance: carMaintenance,
                specs: normalizeVehicleSpecs(phaseSpecs),
                performanceMods: carPerformanceMods,
                suspensionMods: carSuspensionMods,
                wheelTireMods: carWheelTireMods,
                exteriorMods: carExteriorMods,
                interiorMods: carInteriorMods,
                miscMods: carMiscMods
              }
            );
          }

          return car;
        });

        setGarageCars(updatedCars);
        clearCarForm();

        await saveGarage(updatedCars, false);

        showToast("Build updated and saved.");
        return;
      }

      const newCar = {
        ...plaqueMetadata,
        id: Date.now(),
        ownershipHistory: [
          {
            action: "created",
            username,
            date: new Date().toISOString()
          }
        ],
        year: carYear,
        make: carMake,
        model: carModel,
        purchaseDate,
        name: `${carYear} ${carMake} ${carModel}`,
        details: carDetails,
        status: carStatus,
        oemSpecs: normalizeVehicleSpecs(oemSpecs),
        specs: normalizeVehicleSpecs(phaseSpecs),
        performanceMods: carPerformanceMods,
        suspensionMods: carSuspensionMods,
        wheelTireMods: carWheelTireMods,
        exteriorMods: carExteriorMods,
        interiorMods: carInteriorMods,
        miscMods: carMiscMods,
        mods: carMods,
        maintenance: carMaintenance,
        showBuildCost,
        saleInfo: {
          price: salePrice,
          mileage: saleMileage,
          titleStatus: saleTitleStatus,
          preferredContact: salePreferredContact,
          classifiedsLink: saleClassifiedsLink
        },
        timeline: carTimeline,
        lastUpdated: new Date().toISOString(),
        images: uploadedCarImages
      };

      const updatedGarageCars = [...garageCars, normalizeCarStages(newCar)];

      setGarageCars(updatedGarageCars);
      clearCarForm();

      const saved = await saveGarage(updatedGarageCars, false);
      if (!saved) return;
      try {
        const { buildId } = await ensureVehicleBuildId(profileDocId || username, newCar.id);
        setGarageCars((cars) => cars.map((car) => car.id === newCar.id ? { ...car, buildId } : car));
        showToast("Build saved to garage.");
      } catch {
        showToast("Vehicle saved; Build ID pending", "Open Vehicle Plaque to retry secure Build ID assignment.", "warning");
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const createBuildChapter = async (carId, chapterData) => {
    try {
      const newStageId = `stage-${Date.now()}`;

      const updatedGarageCars = garageCars.map((car) => {
        if (String(car.id) !== String(carId)) {
          return car;
        }

        const normalizedCar = normalizeCarStages(car);
        const stages = normalizedCar.stages || [];
        const currentStage =
          stages.find((stage) => stage.id === normalizedCar.currentStageId) ||
          stages[0];

        if (!currentStage) {
          return normalizedCar;
        }

        const newStage = {
          ...currentStage,
          id: newStageId,
          name: chapterData.name,
          isCurrent: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          specs: normalizeVehicleSpecs(currentStage.specs),
          images: chapterData.copyPhotos ? currentStage.images || [] : [],
          mods: (() => {
            if (!chapterData.selectedMods || chapterData.selectedMods.length === 0) {
              return chapterData.copyMods ? currentStage.mods || [] : [];
            }

            return chapterData.selectedMods;
          })(),
          timeline: chapterData.copyTimeline ? currentStage.timeline || [] : [],
          maintenance: currentStage.maintenance || []
        };

        const updatedStages = stages.map((stage) => ({
          ...stage,
          isCurrent: false
        }));

        const updatedCar = {
          ...normalizedCar,
          currentStageId: newStageId,
          stages: [...updatedStages, newStage],
          images: newStage.images,
          mods: newStage.mods,
          timeline: newStage.timeline,
          maintenance: newStage.maintenance,
          performanceMods: newStage.performanceMods || "",
          suspensionMods: newStage.suspensionMods || "",
          wheelTireMods: newStage.wheelTireMods || "",
          exteriorMods: newStage.exteriorMods || "",
          interiorMods: newStage.interiorMods || "",
          miscMods: newStage.miscMods || "",
          lastUpdated: new Date().toISOString()
        };

        if (String(editingCarId) === String(carId)) {
          setPhaseSpecs(normalizeVehicleSpecs(newStage.specs));
        }

        return updatedCar;
      });

      setGarageCars(updatedGarageCars);
      await saveGarage(updatedGarageCars, false);

      showToast("Build Phase Created", `${chapterData.name} is now the current Build Phase.`);
    } catch (error) {
      showToast("Build Phase Creation Failed", error.message, "error");
    }
  };

  const switchBuildChapter = async (carId, stageId) => {
    try {
      const updatedGarageCars = garageCars.map((car) => {
        if (String(car.id) !== String(carId)) {
          return car;
        }

        const normalizedCar = normalizeCarStages(car);
        const selectedStage = normalizedCar.stages?.find(
          (stage) => stage.id === stageId
        );

        if (!selectedStage) {
          return normalizedCar;
        }

        if (String(editingCarId) === String(carId)) {
          setExistingCarImages(selectedStage.images || []);
          setCarMods(selectedStage.mods || []);
          setCarTimeline(selectedStage.timeline || []);
          setCarMaintenance(selectedStage.maintenance || []);
          setPhaseSpecs(normalizeVehicleSpecs(selectedStage.specs));
          setCarPerformanceMods(selectedStage.performanceMods || "");
          setCarSuspensionMods(selectedStage.suspensionMods || "");
          setCarWheelTireMods(selectedStage.wheelTireMods || "");
          setCarExteriorMods(selectedStage.exteriorMods || "");
          setCarInteriorMods(selectedStage.interiorMods || "");
          setCarMiscMods(selectedStage.miscMods || "");
        }

        return {
          ...normalizedCar,
          currentStageId: stageId,
          stages: normalizedCar.stages.map((stage) => ({
            ...stage,
            isCurrent: stage.id === stageId
          })),
          images: selectedStage.images || [],
          mods: selectedStage.mods || [],
          timeline: selectedStage.timeline || [],
          maintenance: selectedStage.maintenance || [],
          performanceMods: selectedStage.performanceMods || "",
          suspensionMods: selectedStage.suspensionMods || "",
          wheelTireMods: selectedStage.wheelTireMods || "",
          exteriorMods: selectedStage.exteriorMods || "",
          interiorMods: selectedStage.interiorMods || "",
          miscMods: selectedStage.miscMods || "",
          lastUpdated: new Date().toISOString()
        };
      });

      setGarageCars(updatedGarageCars);
      await saveGarage(updatedGarageCars, false);

      showToast("Build Phase Switched", "This is now the current Build Phase.");
    } catch (error) {
      showToast("Switch Failed", error.message, "error");
    }
  };

  const renameBuildChapter = async (carId, stageId, newName) => {
    try {
      const updatedGarageCars = garageCars.map((car) => {
        if (String(car.id) !== String(carId)) {
          return car;
        }

        return {
          ...normalizeCarStages(car),
          stages: (car.stages || []).map((stage) =>
            stage.id === stageId
              ? {
                  ...stage,
                  name: newName,
                  updatedAt: new Date().toISOString()
                }
              : stage
          ),
          lastUpdated: new Date().toISOString()
        };
      });

      setGarageCars(updatedGarageCars);
      await saveGarage(updatedGarageCars, false);

      showToast("Build Phase Renamed", "Build Phase name updated.");
    } catch (error) {
      showToast("Rename Failed", error.message, "error");
    }
  };

  const editVehicleDetails = (car) => {
    editGarageCar(car);
    setVehicleDetailsOpen(true);
  };

  const editGarageCar = (car) => {
    const normalizedCar = normalizeCarStages(car);
    const currentStage = getCurrentStage(normalizedCar);

    setEditingCarId(normalizedCar.id);
    setCarYear(normalizedCar.year || "");
    setCarMake(normalizedCar.make || "");
    setCarModel(normalizedCar.model || "");
    setPlaqueMetadata(Object.fromEntries(Object.keys(EMPTY_PLAQUE_METADATA).map((key) => [key, normalizedCar[key] || ""])));
    setPurchaseDate(normalizedCar.purchaseDate || "");
    setCarDetails(normalizedCar.details || "");
    setCarStatus(normalizedCar.status || "Driven");
    setShowBuildCost(normalizedCar.showBuildCost || false);

    setOemSpecs(normalizeVehicleSpecs(normalizedCar.oemSpecs));
    setPhaseSpecs(normalizeVehicleSpecs(currentStage.specs));

    setCarMods(currentStage.mods || []);

    setCarPerformanceMods(
      currentStage.performanceMods ||
        normalizedCar.performanceMods ||
        normalizedCar.engineMods ||
        ""
    );

    setCarSuspensionMods(
      currentStage.suspensionMods ||
        normalizedCar.suspensionMods ||
        ""
    );

    setCarWheelTireMods(
      currentStage.wheelTireMods ||
        normalizedCar.wheelTireMods ||
        normalizedCar.wheelMods ||
        ""
    );

    setCarExteriorMods(
      currentStage.exteriorMods ||
        normalizedCar.exteriorMods ||
        ""
    );

    setCarInteriorMods(
      currentStage.interiorMods ||
        normalizedCar.interiorMods ||
        ""
    );

    setCarMiscMods(
      currentStage.miscMods ||
        normalizedCar.miscMods ||
        ""
    );

    setCarImages([]);
    setExistingCarImages(currentStage.images || []);
    setCarTimeline(currentStage.timeline || []);
    setEditingTimelineId(null);

    setCarMaintenance(currentStage.maintenance || normalizedCar.maintenance || []);
    setEditingMaintenanceId(null);

    setEditingModId(null);

    setSalePrice(normalizedCar.saleInfo?.price || "");
    setSaleMileage(normalizedCar.saleInfo?.mileage || "");
    setSaleTitleStatus(normalizedCar.saleInfo?.titleStatus || "");
    setSalePreferredContact(normalizedCar.saleInfo?.preferredContact || "");
    setSaleClassifiedsLink(normalizedCar.saleInfo?.classifiedsLink || "");

    setBuildOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const removeGarageCar = async (carId) => {
    const updatedGarage = garageCars.filter((car) => car.id !== carId);

    setGarageCars(updatedGarage);

    await saveGarage(updatedGarage, false);
  };

  const saveGarage = async (garageOverride = garageCars, showAlert = true) => {
    try {
      const user = auth.currentUser;

      if (!user) {
        showToast("You need to be logged in first.", "", "error");
        return;
      }

      let profileId = profileDocId || username?.toLowerCase();

      if (!profileId) {
        const profileQuery = query(
          collection(db, "profiles"),
          where("userId", "==", user.uid)
        );

        const snapshot = await getDocs(profileQuery);

        if (!snapshot.empty) {
          profileId = snapshot.docs[0].id;
          setProfileDocId(profileId);
        }
      }

      if (!profileId) {
        showToast("Profile missing. Please save your account first.", "", "error");
        return;
      }

      await setDoc(
        doc(db, "profiles", profileId),
        {
          garageCars: garageOverride.map(normalizeCarStages),
          userId: user.uid
        },
        { merge: true }
      );

      if (showAlert) {
        showToast("Garage saved.");
      }
      return true;
    } catch (error) {
      alert(error.message);
    }
  };

  const handleAddVehicle = () => {
    if (!buildOpen) {
      if (
        !editingCarId &&
        !profileLoading &&
        !canCreateBuild(profile, garageCars.length)
      ) {
        setShowUpgradeModal(true);
        return;
      }
      clearCarForm();
      setVehicleDetailsOpen(true);
    }

    setBuildOpen(!buildOpen);
  };

  return (
    <>
      <PageLayout maxWidth="520px">
        <div style={cardStyle}>
          {!profileLoading && !profileDocId && (
            <div style={accountRequiredStyle}>
              <h3 style={{ marginTop: 0 }}>Finish Account Setup</h3>

              <p style={{ opacity: 0.8, lineHeight: "1.6" }}>
                Before adding a build, create your username and profile
                information.
              </p>

              <button
                type="button"
                onClick={() => navigate("/account")}
                style={buttonStyle}
              >
                Go to Account
              </button>
            </div>
          )}

          {profileDocId && (
            <>
              <div style={pageHeaderStyle}>
                <div>
                  <h1 style={pageTitleStyle}>Garage</h1>
                  {!profileLoading && (
                    <p style={usageStyle}>
                      <strong>{garageCars.length} {garageCars.length === 1 ? "build" : "builds"}</strong>
                      {!hasUnlimitedGarage && garageCars.length >= 1 && (
                        <span>Free plan limit reached</span>
                      )}
                    </p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleAddVehicle}
                  style={smallAddBuildButtonStyle}
                >
                  {buildOpen
                    ? "Close Editor"
                    : editingCarId
                    ? "Continue Editing"
                    : "+ Add Vehicle"}
                </button>
              </div>

              {buildOpen && (
                <div style={sectionCardStyle}>
                  <VehiclePlaqueMetadataFields value={plaqueMetadata} onChange={setPlaqueMetadata} />
                  <GarageEditor
                    carYear={carYear}
                    setCarYear={setCarYear}
                    carMake={carMake}
                    setCarMake={setCarMake}
                    carModel={carModel}
                    setCarModel={setCarModel}
                    purchaseDate={purchaseDate}
                    setPurchaseDate={setPurchaseDate}
                    carStatus={carStatus}
                    setCarStatus={setCarStatus}
                    hasUnlimitedGarage={hasUnlimitedGarage}
                    salePrice={salePrice}
                    setSalePrice={setSalePrice}
                    saleMileage={saleMileage}
                    setSaleMileage={setSaleMileage}
                    saleTitleStatus={saleTitleStatus}
                    setSaleTitleStatus={setSaleTitleStatus}
                    salePreferredContact={salePreferredContact}
                    setSalePreferredContact={setSalePreferredContact}
                    saleClassifiedsLink={saleClassifiedsLink}
                    setSaleClassifiedsLink={setSaleClassifiedsLink}
                    carDetails={carDetails}
                    setCarDetails={setCarDetails}
                    oemSpecs={oemSpecs}
                    setOemSpecs={setOemSpecs}
                    phaseSpecs={phaseSpecs}
                    setPhaseSpecs={setPhaseSpecs}
                    currentPhaseName={currentPhaseName}
                    modCategories={modCategories}
                    addModEntry={addModEntry}
                    editingModId={editingModId}
                    carMods={carMods}
                    moveModUp={moveModUp}
                    moveModDown={moveModDown}
                    editModEntry={editModEntry}
                    openConfirmModal={openConfirmModal}
                    deleteModEntry={deleteModEntry}
                    timelineDate={timelineDate}
                    setTimelineDate={setTimelineDate}
                    timelineMod={timelineMod}
                    setTimelineMod={setTimelineMod}
                    addTimelineEntry={addTimelineEntry}
                    editingTimelineId={editingTimelineId}
                    carTimeline={carTimeline}
                    editTimelineEntry={editTimelineEntry}
                    deleteTimelineEntry={deleteTimelineEntry}
                    carMaintenance={carMaintenance}
                    editingMaintenanceId={editingMaintenanceId}
                    addMaintenanceEntry={addMaintenanceEntry}
                    editMaintenanceEntry={editMaintenanceEntry}
                    deleteMaintenanceEntry={deleteMaintenanceEntry}
                    setCarImages={setCarImages}
                    existingCarImages={existingCarImages}
                    deleteExistingCarImage={deleteExistingCarImage}
                    showBuildCost={showBuildCost}
                    setShowBuildCost={setShowBuildCost}
                    addOrUpdateGarageCar={addOrUpdateGarageCar}
                    ownerId={auth.currentUser?.uid || ""}
                    username={username}
                    carId={editingCarId ? String(editingCarId) : ""}
                    isOwner={Boolean(auth.currentUser && editingCarId)}
                  />
                </div>
              )}

              {editingCarId && (
                <button type="button" onClick={clearCarForm} style={neutralButtonStyle}>
                  Cancel Edit
                </button>
              )}

              <GarageList
                auth={auth}
                username={username}
                garageCars={garageCars}
                showToast={showToast}
                navigate={navigate}
                editGarageCar={editGarageCar}
                openConfirmModal={openConfirmModal}
                removeGarageCar={removeGarageCar}
                createBuildChapter={createBuildChapter}
                hasUnlimitedGarage={hasUnlimitedGarage}
                switchBuildChapter={switchBuildChapter}
                renameBuildChapter={renameBuildChapter}
                editVehicleDetails={editVehicleDetails}
                onAddVehicle={handleAddVehicle}
              />
            </>
          )}
        </div>
      </PageLayout>

      <UpgradeLimitModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />

      <Toast
        title={toast.title}
        message={toast.message}
        type={toast.type}
        onClose={() =>
          setToast({
            title: "",
            message: "",
            type: "success"
          })
        }
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        cancelText="Cancel"
        danger={confirmModal.danger}
        onCancel={closeConfirmModal}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }

          closeConfirmModal();
        }}
      />

      <VehicleInfoModal
        plaqueMetadata={plaqueMetadata}
        setPlaqueMetadata={setPlaqueMetadata}
        isOpen={vehicleDetailsOpen}
        carYear={carYear}
        setCarYear={setCarYear}
        carMake={carMake}
        setCarMake={setCarMake}
        carModel={carModel}
        setCarModel={setCarModel}
        purchaseDate={purchaseDate}
        setPurchaseDate={setPurchaseDate}
        carStatus={carStatus}
        setCarStatus={setCarStatus}
        carDetails={carDetails}
        setCarDetails={setCarDetails}
        salePrice={salePrice}
        setSalePrice={setSalePrice}
        saleMileage={saleMileage}
        setSaleMileage={setSaleMileage}
        saleTitleStatus={saleTitleStatus}
        setSaleTitleStatus={setSaleTitleStatus}
        salePreferredContact={salePreferredContact}
        setSalePreferredContact={setSalePreferredContact}
        saleClassifiedsLink={saleClassifiedsLink}
        setSaleClassifiedsLink={setSaleClassifiedsLink}
        onSave={saveVehicleDetails}
        onCancel={() => setVehicleDetailsOpen(false)}
      />
    </>
  );
}

const cardStyle = {
  backgroundColor: "#181818",
  border: "1px solid #242424",
  padding: "20px",
  borderRadius: "20px",
  width: "100%",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  gap: "14px"
};

const sectionCardStyle = {
  backgroundColor: "#1a1a1a",
  border: "1px solid #242424",
  borderRadius: "16px",
  overflow: "hidden",
  boxSizing: "border-box"
};

const buttonStyle = {
  padding: "14px",
  border: "none",
  borderRadius: "12px",
  cursor: "pointer",
  fontWeight: "bold",
  backgroundColor: "#e10600",
  color: "white"
};

const neutralButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#333"
};

const accountRequiredStyle = {
  backgroundColor: "#1f1f1f",
  border: "1px solid #333",
  borderRadius: "14px",
  padding: "20px",
  marginBottom: "18px",
  textAlign: "center",
  boxSizing: "border-box"
};

const smallAddBuildButtonStyle = {
  backgroundColor: "#e10600",
  color: "white",
  border: "none",
  borderRadius: "999px",
  padding: "9px 14px",
  fontWeight: "bold",
  fontSize: "13px",
  cursor: "pointer",
  whiteSpace: "nowrap"
};

const pageHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  marginBottom: "8px"
};

const pageTitleStyle = {
  margin: 0,
  fontSize: "clamp(32px, 9vw, 42px)",
  color: "white"
};

const usageStyle = { display: "grid", gap: "3px", margin: "5px 0 0", color: "#aaa", fontSize: "13px", lineHeight: 1.35 };

export default Garage;
